// AccessSerialPage.js - with URL sync
import { observer } from 'mobx-react-lite';
import Layout from '../Layout/Layout';
import { useEffect, useState, useCallback } from 'react';
import {
    Box, Typography, Snackbar, Alert, CircularProgress, Button,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import axios from 'axios';
import AccessSerialFilterPanel from './AccessSerialFilterPanel';
import AccessSerialTable from './AccessSerialTable';
import AddAccessSerial from './AddAccessSerial';
import UpdateAccessSerial from './UpdateAccessSerial';
import urls from '../../Mobx/Urls';
import { useLocation } from 'react-router-dom';
import { parseQueryParams, updateUrlParams } from '../../Utils/urlUtils';
import { generateLink } from '../../Utils/link';

const AccessSerialPage = observer(() => {
    const location = useLocation();

    const {
        filters: initialFilters,
        sortField: initialSortField,
        sortOrder: initialSortOrder,
        page: initialPage,
        limit: initialLimit
    } = parseQueryParams(new URLSearchParams(location.search), ['name', 'email', 'serial', 'permissionProfile'], 'date');

    const [filters, setFilters] = useState(initialFilters);
    const [sortField, setSortField] = useState(initialSortField);
    const [sortOrder, setSortOrder] = useState(initialSortOrder);
    const [page, setPage] = useState(initialPage);
    const [serials, setSerials] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [notificationMessage, setNotificationMessage] = useState(null);
    const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [editSerial, setEditSerial] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    // ---------------
    const LIMIT_KEY = 'limitCustomerPage';
    const [limit, setLimit] = useState(() => {
        const storedLimit = localStorage.getItem(LIMIT_KEY);
        return storedLimit ? Number(storedLimit) : initialLimit;
    });
    useEffect(() => {
        localStorage.setItem(LIMIT_KEY, limit);
    }, [limit]);
    // ---------------

    const fetchSerials = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await axios.post(urls.accessSerial.getAllWithPagination, {
                page,
                limit,
                filters,
                sort: [{ field: sortField, order: sortOrder }]
            }, { withCredentials: true });

            setSerials(response.data.data);
            setTotal(response.data.total);
        } catch (error) {
            console.error('Failed to fetch serials:', error);
            setErrorMessage(`שגיאה בטעינת הסיריאלים: ${error?.response?.data}`);
            setIsSnackbarOpen(true);
        } finally {
            setIsLoading(false);
        }
    }, [page, limit, filters, sortField, sortOrder]);

    const handleDelete = (serial) => {
        setDeleteTarget(serial);
        setIsConfirmOpen(true);
    };

    const handleLink = (serial) => {
        navigator.clipboard.writeText( generateLink(urls.linkUrl, serial.serial,urls.frontendUrl) );
        setNotificationMessage(`קוד הסיריאל הועתק ללוח: ${serial.serial}`);
        setIsSnackbarOpen(true);
    }

    const confirmDelete = async () => {
        try {
            await axios.delete(`${urls.accessSerial.delete}/${deleteTarget._id}`, { withCredentials: true });
            setIsConfirmOpen(false);
            setDeleteTarget(null);
            fetchSerials();
        } catch (error) {
            setErrorMessage(`שגיאה במחיקת הסיריאל: ${error?.response?.data}`);
            setIsSnackbarOpen(true);
            setIsConfirmOpen(false);
        }
    };

    useEffect(() => {
        updateUrlParams({ filters, sortField, sortOrder, page, limit, dateKey: 'date' });
        fetchSerials();
    }, [filters, sortField, sortOrder, page, limit, fetchSerials]);

    const tools = [
        <Box
            key="total-users"
            sx={{
                backgroundColor: 'white', px: 2, py: 1,
                borderRadius: 2, boxShadow: 1,
                display: 'inline-block', fontWeight: 'bold',
                color: 'primary.main', border: '1px solid #ccc',
                minWidth: '220px', textAlign: 'center'
            }}
        >
            <Typography variant="body2">
                מספר סיריאלים שנמצאו: {total}
            </Typography>
        </Box>,
        <Button key="add-button" variant="contained" color="primary" onClick={() => setIsAddOpen(true)}>
            הוספת סיריאל
        </Button>
    ];

    const drawerTools = [
        <AccessSerialFilterPanel
            key="filter-panel"
            filters={filters}
            setFilters={setFilters}
            sortField={sortField}
            setSortField={setSortField}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            fetchSerials={fetchSerials}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
        />
    ];

    const handleCloseSnackbar = () => setIsSnackbarOpen(false);

    return (
        <div>
            <Layout tools={tools} drawerTools={drawerTools} toolsConfig={{ toolsHeight: 45, toolsBackgroundColor: '#FFFFFF', drawerToolsWidth: 400 }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: `calc(100vh - 95px)` }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ height: `calc(100vh - 95px)`, overflowY: 'auto', flexGrow: 1 }}>
                        <AccessSerialTable
                            serials={serials}
                            total={total}
                            page={page}
                            limit={limit}
                            setPage={setPage}
                            onEdit={setEditSerial}
                            onDelete={handleDelete}
                            onLink={handleLink}
                        />
                    </Box>
                )}
            </Layout>

            <AddAccessSerial open={isAddOpen} onClose={() => setIsAddOpen(false)} onSuccess={fetchSerials} />
            <UpdateAccessSerial serial={editSerial} onClose={() => setEditSerial(null)} onSuccess={fetchSerials} />

            <Snackbar
                open={isSnackbarOpen}
                autoHideDuration={notificationMessage ? 3000 : null}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {notificationMessage ? (
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                        {notificationMessage}
                    </Alert>
                ) : (
                <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
                    {errorMessage}
                </Alert>
                )}
            </Snackbar>

            <Dialog open={isConfirmOpen} onClose={() => setIsConfirmOpen(false)}>
                <DialogTitle>אישור מחיקה</DialogTitle>
                <DialogContent>
                    <Typography>האם אתה בטוח שברצונך למחוק את הסיריאל "{deleteTarget?.serial}"?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsConfirmOpen(false)}>ביטול</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">מחק</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
});

export default AccessSerialPage;